import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule } from '@angular/core';
import { IonicApp, IonicErrorHandler, IonicModule } from 'ionic-angular';
import { SplashScreen } from '@ionic-native/splash-screen';
import { IonicStorageModule } from '@ionic/storage';
import { SocialSharing } from '@ionic-native/social-sharing';
import { StatusBar } from '@ionic-native/status-bar';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { Camera } from '@ionic-native/camera';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { OneSignal } from '@ionic-native/onesignal';

import { HttpModule } from '@angular/http';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';

// SVG Round Progressbar
import { RoundProgressModule } from 'angular-svg-round-progressbar';

// CSS Star Rating
import { StarRatingModule } from "angular-star-rating";

// Translate
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// ngx Pipes
import { NgMathPipesModule } from "ngx-pipes";

// Pipes
import { DateFormatPipe } from "../pipes/date-format/date-format";
import { ExcerptPipe } from "../pipes/excerpt/excerpt";
import { Nl2brPipe } from "../pipes/nl2br/nl2br";
import { ShopFilterPipe } from "../pipes/shop-filter/shop-filter";

// Directives
import { DirectivesModule } from "../directives/directives.module";

// Components
import { ComponentsModule } from "../components/components.module";

// App
import { AskMarcApp } from './app.component';

// Pages
import { LoginPage } from '../pages/auth/login';
import { RegisterPage } from '../pages/auth/register';
import { ForgottenPasswordPage } from '../pages/auth/forgotten-password';
import { WelcomePage } from '../pages/welcome/welcome';
import { BrandPage } from "../pages/brand/brand";
import { BrandSelectorPage } from "../pages/brand-selector/brand-selector";
import { ProductCategoriesPage } from "../pages/product-categories/product-categories";
import { ProductsPage } from '../pages/products/products';
import { ProductDetailPage } from '../pages/product-detail/product-detail';
import { PackshotsPage } from '../pages/packshots/packshots';
import { ChallengesPage } from '../pages/challenges/challenges';
import { ChallengeDetailPage } from '../pages/challenge-detail/challenge-detail';
import { PostsPage } from '../pages/posts/posts';
import { PostDetailPage } from '../pages/post-detail/post-detail';
import { PostCommentsPage } from '../pages/post-comments/post-comments';
import { PublicPostDetailPage } from '../pages/public-post-detail/public-post-detail';

import { BasketPage } from '../pages/basket/basket';
import { OrderPage } from '../pages/order/order';
import { OrderDetailPage } from '../pages/order-detail/order-detail';
import { OrdersListPage } from '../pages/orders-list/orders-list';
import { PosmCategoriesPage } from '../pages/posm-categories/posm-categories';
import { PosmItemsPage } from '../pages/posm-items/posm-items';
import { PosmSubitemsPage } from '../pages/posm-subitems/posm-subitems';
import { PosmSubitemDescriptionPage } from '../pages/posm-subitem-description/posm-subitem-description';

import { QuizzesPage } from '../pages/quizzes/quizzes';
import { QuizDetailPage } from "../pages/quiz-detail/quiz-detail";
import { IncidentReportingPage } from '../pages/incident-reporting/incident-reporting';
import { ProfilePage } from '../pages/profile/profile';
import { ProfileAvatarPage } from '../pages/profile-avatar/profile-avatar';
import { ProfileEditPage } from "../pages/profile-edit/profile-edit";
import { RegisterUserPage } from "../pages/register-user/register-user";
import { PrivacyPolicyPage } from "../pages/privacy-policy/privacy-policy";
import { TermsOfUsePage } from "../pages/terms-of-use/terms-of-use";
import { RetailersPage } from "../pages/retailers/retailers";
import { ShopsPage } from "../pages/shops/shops";
import { RulesPage } from "../pages/rules/rules";
import { RankProgressPage } from "../pages/rank-progress/rank-progress";
import { NotificationsPage } from "../pages/notifications/notifications";

// Modules/pages
import { ResetPasswordPageModule } from "../pages/auth/reset-password.module";

// Providers
import { BrandProvider } from '../providers/brand/brand';
import { ChallengeProvider } from '../providers/challenge/challenge';
import { EntryProvider } from '../providers/entry/entry';
import { PostProvider } from '../providers/post/post';
import { PostCommentProvider } from '../providers/post-comment/post-comment';
import { QuizProvider } from '../providers/quiz/quiz';
import { AuthProvider } from '../providers/auth/auth';
import { ProductCategoryProvider } from "../providers/product-category/product-category";

import { OrderProvider } from "../providers/order/order";
import { PosmCategoryProvider } from "../providers/posm-category/posm-category";
import { PosmItemProvider } from "../providers/posm-item/posm-item";
import { PosmSubitemProvider } from "../providers/posm-subitem/posm-subitem";

import { ProductProvider } from '../providers/product/product';
import { AuthInterceptor } from '../interceptors/auth.interceptor';
import { CountryProvider } from '../providers/country/country';
import { RetailerProvider } from '../providers/retailer/retailer';
import { ShopProvider } from '../providers/shop/shop';
import { UserProvider } from '../providers/user/user';
import { QuestionProvider } from '../providers/question/question';
import { LanguageProvider } from '../providers/language/language';
import { ScenarioProvider } from '../providers/scenario/scenario';
import { IncidentProvider } from '../providers/incident/incident';
import { ExternalHTMLProvider } from "../providers/external-html/external-html";
import { RankProvider } from "../providers/rank/rank";
import { NotificationProvider } from "../providers/notification/notification";

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    DateFormatPipe,
    ExcerptPipe,
    Nl2brPipe,
    ShopFilterPipe,
    AskMarcApp,
    LoginPage,
    RegisterPage,
    ForgottenPasswordPage,
    WelcomePage,
    BrandPage,
    BrandSelectorPage,
    ProductCategoriesPage,
    ProductsPage,
    ProductDetailPage,
    PackshotsPage,
    ChallengesPage,
    ChallengeDetailPage,
    PostsPage,
    PostDetailPage,
    PostCommentsPage,
    PublicPostDetailPage,
    BasketPage,
    OrderPage,
    OrderDetailPage,
    OrdersListPage,
    PosmCategoriesPage,
    PosmItemsPage,
    PosmSubitemsPage,
    PosmSubitemDescriptionPage,
    QuizzesPage,
    QuizDetailPage,
    IncidentReportingPage,
    ProfilePage,
    ProfileAvatarPage,
    ProfileEditPage,
    RegisterUserPage,
    PrivacyPolicyPage,
    TermsOfUsePage,
    RetailersPage,
    ShopsPage,
    RulesPage,
    RankProgressPage,
    NotificationsPage
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpModule,
    HttpClientModule,
    RoundProgressModule,
    StarRatingModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    NgMathPipesModule,
    IonicStorageModule.forRoot(),
    IonicModule.forRoot(AskMarcApp, {
      scrollAssist: false,
      autoFocusAssist: false
    }, {
      links: [
        { component: PublicPostDetailPage, name: 'PublicPostDetail', segment: 'post-details/:id' }
      ]
    }),
    ResetPasswordPageModule,
    ComponentsModule,
    DirectivesModule
  ],
  bootstrap: [IonicApp],
  entryComponents: [
    AskMarcApp,
    LoginPage,
    RegisterPage,
    ForgottenPasswordPage,
    WelcomePage,
    BrandPage,
    BrandSelectorPage,
    ProductCategoriesPage,
    ProductsPage,
    ProductDetailPage,
    PackshotsPage,
    ChallengesPage,
    ChallengeDetailPage,
    PostsPage,
    PostDetailPage,
    PostCommentsPage,
    PublicPostDetailPage,
    BasketPage,
    OrderPage,
    OrderDetailPage,
    OrdersListPage,
    PosmCategoriesPage,
    PosmItemsPage,
    PosmSubitemsPage,
    PosmSubitemDescriptionPage,
    QuizzesPage,
    QuizDetailPage,
    IncidentReportingPage,
    ProfilePage,
    ProfileAvatarPage,
    ProfileEditPage,
    RegisterUserPage,
    PrivacyPolicyPage,
    TermsOfUsePage,
    RetailersPage,
    ShopsPage,
    RulesPage,
    RankProgressPage,
    NotificationsPage
  ],
  providers: [
    StatusBar,
    SplashScreen,
    ScreenOrientation,
    Camera,
    InAppBrowser,
    SocialSharing,
    {provide: ErrorHandler, useClass: IonicErrorHandler},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    OneSignal,
    LanguageProvider,
    AuthProvider,
    BrandProvider,
    ChallengeProvider,
    EntryProvider,
    PostProvider,
    PostCommentProvider,
    QuizProvider,
    QuestionProvider,
    ProductCategoryProvider,
    OrderProvider,
    PosmCategoryProvider,
    PosmItemProvider,
    PosmSubitemProvider,
    ProductProvider,
    CountryProvider,
    RetailerProvider,
    ShopProvider,
    UserProvider,
    ScenarioProvider,
    IncidentProvider,
    ExternalHTMLProvider,
    RankProvider,
    NotificationProvider
  ]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts